import { auth } from '../config/firebase';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Get Firebase ID token
const getAuthToken = async () => {
  // First try to get fresh token from Firebase Auth
  const currentUser = auth.currentUser;
  if (currentUser) {
    try {
      const token = await currentUser.getIdToken();
      localStorage.setItem('token', token);
      return token;
    } catch (error) {
      console.error('Error getting Firebase token:', error);
    }
  }

  // Fallback to stored token
  const token = localStorage.getItem('token');
  if (token) {
    return token;
  }

  console.warn('No authentication token available');
  return null;
};

// Create API request helper
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = await getAuthToken();

  // Prepare headers
  let headers = {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  // Only set Content-Type if not skipped (for FormData uploads)
  if (!options.skipContentType && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const config = {
    headers,
    ...options,
  };

  console.log('API Request:', {
    method: config.method || 'GET',
    url,
    hasAuth: !!token,
    body: config.body ? (config.body instanceof FormData ? 'FormData' : 'JSON') : 'None'
  });

  try {
    const response = await fetch(url, config);

    console.log('API Response Status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;

      console.error('API Error:', {
        status: response.status,
        message: errorMessage,
        url
      });

      // Handle authentication errors
      if (response.status === 401) {
        console.warn('Authentication failed');

        // Try to refresh token
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            const newToken = await currentUser.getIdToken(true);
            localStorage.setItem('token', newToken);
            // Retry the request with new token
            headers.Authorization = `Bearer ${newToken}`;
            const retryResponse = await fetch(url, { ...config, headers });
            if (retryResponse.ok) {
              return await retryResponse.json();
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }
        }

        throw new Error('Authentication required. Please log in again.');
      }

      // Handle permission errors
      if (response.status === 403) {
        throw new Error('Permission denied. You do not have access to this resource.');
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('API Success:', { endpoint, dataLength: Array.isArray(data?.proverbs) ? data.proverbs.length : 'N/A' });
    return data;
  } catch (error) {
    console.error('API Request Failed:', {
      endpoint,
      error: error.message,
      url
    });
    throw error;
  }
};

export { apiRequest, API_BASE_URL };
